<script lang="ts">
  import { intersection } from "@opera-gx/common/src/actions/intersection";
  import { writable } from "svelte/store";

  import FeaturedGameTile from "../components/Games/FeaturedGameTile.svelte";
  import GameTile from "../components/Games/GameTile.svelte";
  import ShowMore from "../components/ShowMore/ShowMore.svelte";
  import { playedGames } from "../store/playedGames";
  export let items;
  export let lazy = true;
  export let section_title = "";
  export let showMore = writable(false);

  $: featuredGames = items.filter(({ featured }) => featured);
  $: nonFeaturedGames = items.filter(({ featured }) => !featured);

  function sortGames() {
    items.sort((a: { url: string }, b: { url: string }) => {
      const aIndex = $playedGames.games.findIndex((game) => game.url === a.url);
      const bIndex = $playedGames.games.findIndex((game) => game.url === b.url);

      if (aIndex === -1 && bIndex === -1) {
        return 0;
      } else if (aIndex === -1) {
        return 1;
      } else if (bIndex === -1) {
        return -1;
      } else {
        const aTimestamp = new Date($playedGames.games[aIndex]!.timestamp);
        const bTimestamp = new Date($playedGames.games[bIndex]!.timestamp);

        return bTimestamp - aTimestamp;
      }
    });
  }

  const limit = 4;

  const handleOpenGame = (htmlGame, key) => () => {
    // NOTE(duanei): At first glance, it looks like we're trying to trigger this after the
    // page is has unfocused. But that's the thing, gx corner seem to never stop being focused.
    // That means we can't wait until the page is refocused to reload the content, but
    // we still need to avoid the flash that happens if we do it immediately.
    setTimeout(() => playedGames.addPlay(htmlGame, key), 500);
  };

  if ($playedGames?.games?.length > 0) {
    sortGames();
  }

  let headerIntersecting = false;
  let showMoreIntersecting = false;
</script>

<style lang="postcss">
  /* TODO(duanei): All these transitions functions should be move into a separate component so we
     can stick to tailwind here. */
  .title {
    transition:
      opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
      transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    opacity: 0;
    transform: translateX(-15px);

    &.show {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .description {
    transition:
      opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
      transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    opacity: 0;
    transform: translateX(-15px);
    transition-delay: 100ms;
    will-change: opacity, transform;

    &.show {
      opacity: 1;
      transform: translateX(0);
    }
  }
</style>

{#if items}
  <section class="pl-left pr-right flex flex-col gap-4">
    <header
      class="flex flex-col gap-1"
      use:intersection={{
        handler: (entries) => {
          if (!entries.some(({ isIntersecting }) => isIntersecting)) {
            headerIntersecting = false;
            return;
          }

          if (entries.some(({ intersectionRatio }) => intersectionRatio >= 0.05)) {
            headerIntersecting = true;
            return;
          }
        },
        rootMargin: "100% 0px 0px 0px",
        threshold: [0, 0.05],
      }}>
      <h2
        class="title text-lg font-semibold leading-5"
        class:show={headerIntersecting}
        style="color: hsl(var(--color-gxm-text-on-wallpaper));">
        {section_title || ""}
      </h2>
      {#if items.description}
        <span class="description text-sm font-normal" class:show={headerIntersecting}
          >{items.description}</span>
      {/if}
    </header>

    {#if featuredGames.length > 0}
      <div class="gap- flex flex-col">
        {#each featuredGames as item (item.id)}
          <FeaturedGameTile
            {lazy}
            badge={item?.tag}
            href={item.url}
            imageUrl={item.game.thumbnailMobileBig?.url}
            title={item.game.title}
            on:click={handleOpenGame(item)} />
        {/each}
      </div>
    {/if}
    {#if nonFeaturedGames.length > 0}
      <div class="box-border grid grid-cols-2 grid-rows-1 gap-4">
        {#each $showMore ? nonFeaturedGames : nonFeaturedGames.slice(0, limit) as item, index (item.id)}
          <GameTile
            {lazy}
            href={item.url}
            imageUrl={item.game.thumbnailMobileBig?.url}
            title={item.title}
            badge={item?.tag}
            on:click={handleOpenGame(item)}
            {index} />
        {/each}
      </div>

      {#if nonFeaturedGames.length > limit}
        <ShowMore {showMore} {showMoreIntersecting} {section_title} />
      {/if}
    {/if}
  </section>
{/if}
